import { useQuery, useQueryClient } from '@tanstack/react-query';
import { voidPurchaseSchema, type PurchaseDetailDTO } from '@bifeai/shared';
import { Badge, Button, Callout, Card, CardBody, CardHeader, Dialog, EmptyState, Field, PageHeader, Skeleton, Textarea, useToast } from '@bifeai/ui';
import { ArrowLeft, Ban, ReceiptText } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage, isApiError } from '../../lib/api';
import { invalidateInventory } from '../../lib/catalog';
import { formatDate, formatDateTime, formatMoney } from '../../lib/format';
import { daysUntil, formatQty, priceUnit } from '../../lib/units';

export function PurchaseDetailPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const [voiding, setVoiding] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const purchase = useQuery({
    queryKey: ['purchases', 'detail', id],
    queryFn: () => api.get<PurchaseDetailDTO>(`/purchases/${id}`),
    retry: (n, err) => !isApiError(err, 'NOT_FOUND', 'VALIDATION_FAILED') && n < 2,
  });

  async function doVoid() {
    const parsed = voidPurchaseSchema.safeParse({ reason });
    if (!parsed.success) return setReasonError(parsed.error.issues[0]?.message ?? 'Contá el motivo.');
    setBusy(true);
    try {
      await api.post(`/purchases/${id}/void`, parsed.data);
      toast.success('Compra anulada', 'Se descontó del stock.');
      setVoiding(false);
      await invalidateInventory(qc);
    } catch (err) {
      setReasonError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const back = (
    <Link to="/compras">
      <Button variant="ghost" icon={<ArrowLeft />}>
        Compras
      </Button>
    </Link>
  );

  if (purchase.isPending) {
    return (
      <>
        <PageHeader title="Compra" actions={back} />
        <Card>
          <CardBody>
            <div style={{ display: 'grid', gap: 12 }}>
              <Skeleton />
              <Skeleton />
              <Skeleton />
            </div>
          </CardBody>
        </Card>
      </>
    );
  }
  if (purchase.isError || !purchase.data) {
    return (
      <>
        <PageHeader title="Compra" actions={back} />
        <Card>
          <EmptyState icon={<ReceiptText />} title="No encontramos esta compra" text="Puede que sea de otra sucursal o que el enlace esté mal." />
        </Card>
      </>
    );
  }

  const p = purchase.data;
  const owed = p.totalCents - p.paidCents;
  const late = p.status === 'CONFIRMED' && owed > 0 && p.dueDate && daysUntil(p.dueDate) < 0;

  return (
    <>
      <PageHeader
        title={`Compra a ${p.supplierName}`}
        subtitle={`${formatDate(p.date)} · ${p.branchName}${p.invoiceType || p.invoiceNumber ? ` · ${[p.invoiceType, p.invoiceNumber].filter(Boolean).join(' ')}` : ''}`}
        actions={
          <>
            {back}
            {p.status === 'CONFIRMED' && (
              <Button variant="danger" icon={<Ban />} onClick={() => setVoiding(true)} disabled={!p.canVoid}>
                Anular compra
              </Button>
            )}
          </>
        }
      />

      {p.status === 'VOIDED' && (
        <Callout tone="danger">
          <strong>Compra anulada.</strong> {p.voidReason ? `Motivo: ${p.voidReason}.` : ''} Los lotes se descontaron del stock.
        </Callout>
      )}
      {p.fromBrand && (
        <Callout tone="info">
          <strong>Compra a la marca.</strong> La cargó la marca al enviarte la mercadería o facturarte las regalías. Se paga por la cuenta corriente
          con la marca (Mi franquicia); si hay un error, la marca lo corrige con un ajuste.
        </Callout>
      )}
      {p.status === 'CONFIRMED' && !p.canVoid && !p.fromBrand && (
        <Callout>
          Ya se vendió, ajustó o usó mercadería de esta compra, por eso no se puede anular. Si hubo un error, corregí el stock con un ajuste.
        </Callout>
      )}

      <Card>
        <CardBody>
          <dl className="bf-kv">
            <div>
              <dt>Total</dt>
              <dd className="bf-num" style={{ fontSize: '1.25rem' }}>
                {formatMoney(p.totalCents)}
              </dd>
            </div>
            <div>
              <dt>Saldo a pagar</dt>
              <dd className={`bf-num ${owed > 0 ? 'bf-text-warning' : 'bf-text-success'}`}>{formatMoney(Math.max(owed, 0))}</dd>
            </div>
            <div>
              <dt>Vencimiento del pago</dt>
              <dd className={late ? 'bf-text-danger' : undefined}>{p.dueDate ? formatDate(p.dueDate) : 'Contado'}</dd>
            </div>
            <div>
              <dt>Estado</dt>
              <dd>
                {p.status === 'VOIDED' ? (
                  <Badge tone="danger">Anulada</Badge>
                ) : p.fromBrand ? (
                  <>
                    <Badge tone="info">En cuenta corriente</Badge>{' '}
                    <Link to="/mi-franquicia" className="bf-cell-sub">
                      Ver lo que le debés a la marca
                    </Link>
                  </>
                ) : owed <= 0 ? (
                  <Badge tone="success">Pagada</Badge>
                ) : late ? (
                  <Badge tone="danger" dot>
                    Vencida
                  </Badge>
                ) : (
                  <Badge tone="warning" dot>
                    A pagar
                  </Badge>
                )}
              </dd>
            </div>
            <div>
              <dt>Cargada por</dt>
              <dd>{p.createdByName ?? '—'}</dd>
            </div>
            <div>
              <dt>Fecha de carga</dt>
              <dd>{formatDateTime(p.createdAt)}</dd>
            </div>
          </dl>
          {p.notes && <p style={{ marginTop: 16 }}>{p.notes}</p>}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Productos" subtitle="Cada renglón es un lote. “Queda” muestra cuánto de ese lote sigue en stock." />
        <CardBody flush>
          <div className="bf-table-wrap">
            <table className="bf-table">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Cantidad</th>
                  <th>Costo</th>
                  <th>Vence</th>
                  <th>Queda</th>
                  <th style={{ textAlign: 'right' }}>Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {p.lines.map((l) => {
                  const d = l.expiresAt ? daysUntil(l.expiresAt) : null;
                  return (
                    <tr key={l.id}>
                      <td className="bf-cell-main">{l.productName}</td>
                      <td className="bf-num">{formatQty(l.unitType, l.qty)}</td>
                      <td className="bf-num">
                        {formatMoney(l.unitCostCents)}
                        <span className="bf-cell-sub"> {priceUnit(l.unitType)}</span>
                      </td>
                      <td className={d !== null && d < 0 ? 'bf-text-danger' : d !== null && d <= 2 ? 'bf-text-warning' : undefined}>
                        {l.expiresAt ? formatDate(l.expiresAt) : '—'}
                      </td>
                      <td className="bf-num">{l.lotQtyOnHand !== null ? formatQty(l.unitType, l.lotQtyOnHand) : '—'}</td>
                      <td className="bf-num" style={{ textAlign: 'right', fontWeight: 600 }}>
                        {formatMoney(l.lineTotalCents)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      <Dialog
        open={voiding}
        onOpenChange={(o) => !o && !busy && setVoiding(false)}
        title="Anular compra"
        description="Los lotes de esta compra se descuentan del stock. Queda registrado en la auditoría."
      >
        <div className="bf-form">
          <Field label="Motivo" error={reasonError ?? undefined}>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ej.: cargada dos veces" autoFocus />
          </Field>
          <div className="bf-form-actions">
            <Button onClick={() => setVoiding(false)} disabled={busy}>
              Cancelar
            </Button>
            <Button variant="danger" icon={<Ban />} loading={busy} onClick={() => void doVoid()}>
              Anular
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
